/**
 * 유효 권한 보기(읽기 전용) — 도메인 › 탭 › 세부 권한을 사이드바처럼 화살표 + 들여쓰기로 편다.
 *
 * 왜 칩 목록이 아닌가
 *   예전엔 권한 이름을 칩으로 죽 늘어놨는데, 20개만 넘어가도 "이 사람이 무슨 화면을 쓸 수 있는지"가
 *   눈에 안 들어왔다. 바로 위 편집 트리(PermissionTree)와 같은 계층으로 보여주면 편집 화면에서 본
 *   위치 그대로 찾을 수 있다.
 *
 * 출처 배지
 *   같은 권한이 부서·개인 양쪽에서 올 수 있다. 부서 권한은 이 화면에서 뗄 수 없으므로
 *   (부서 권한 설정에서 관리) 행마다 어디서 온 권한인지 표시한다.
 *
 * 모르는 코드도 지우지 않는다
 *   권한 목록(/api/permissions/list)에 없는 코드가 부여돼 있을 수 있다(코드 이름이 바뀐 뒤 남은 행 등).
 *   그런 코드를 화면에서 빼면 "유효 권한 14개"라고 써놓고 12개만 보이는 상태가 된다.
 *   분류를 모르는 코드는 마지막 '기타' 묶음에 코드 그대로 보여준다.
 *
 * props
 *   perms         : [{code, nameKr, description, domain, tab}] — /api/permissions/list 응답
 *   deptCodes     : Set<code> | string[] — 부서 자동 권한
 *   personalCodes : Set<code> | string[] — 개인 권한
 *   emptyText     : 유효 권한이 하나도 없을 때 문구
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { buildPermissionTree } from "./permissionGrouping";
import SubTreeLine from "../common/SubTreeLine";
import { DOMAIN_ROW_PL, EXPAND_ICON, ROW_PR, TREE_INSET } from "./treeLayout";

const SOURCE_STYLE = {
    부서: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    개인: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
};

/** 펼침 화살표 — 행 오른쪽 끝 (편집 트리·사이드바와 같은 모양). */
function Expander({ open }) {
    const Icon = open ? ExpandLessRoundedIcon : ExpandMoreRoundedIcon;
    return <Icon sx={{ fontSize: EXPAND_ICON, color: "#94a3b8", flexShrink: 0 }} />;
}

function SourceBadge({ label }) {
    const s = SOURCE_STYLE[label];
    return (
        <Box sx={{
            px: 0.7, py: 0.1, borderRadius: 0.75, flexShrink: 0,
            border: `1px solid ${s.border}`, bgcolor: s.bg, color: s.color,
            fontSize: 10.5, fontWeight: 700, lineHeight: 1.6,
        }}>
            {label}
        </Box>
    );
}

function Count({ n }) {
    return (
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#475569", flexShrink: 0 }}>
            {n}개
        </Typography>
    );
}

const toSet = (v) => (v instanceof Set ? v : new Set(Array.isArray(v) ? v : []));

export default function EffectivePermissionTree({
    perms,
    deptCodes,
    personalCodes,
    emptyText = "부여된 권한이 없습니다.",
}) {
    const deptSet = useMemo(() => toSet(deptCodes), [deptCodes]);
    const personalSet = useMemo(() => toSet(personalCodes), [personalCodes]);

    const tree = useMemo(() => {
        const isEffective = (code) => deptSet.has(code) || personalSet.has(code);
        const known = buildPermissionTree(perms, { filter: (p) => isEffective(p.code) });

        // 권한 목록에 없는 코드 — 개수를 맞추기 위해 코드 그대로라도 보여준다.
        const covered = new Set((perms || []).map((p) => p.code));
        const orphans = [...new Set([...deptSet, ...personalSet])]
            .filter((c) => !covered.has(c))
            .map((c) => ({ code: c, nameKr: c, domain: "기타", tab: "기타" }));

        return orphans.length
            ? [...known, ...buildPermissionTree(orphans)]
            : known;
    }, [perms, deptSet, personalSet]);

    /**
     * 도메인은 펼친 채로, 탭은 접은 채로 시작한다.
     *
     * <p>부서를 고르자마자 "무슨 도메인 권한이 몇 개 붙는지"가 바로 보여야 하고(그게 이 화면의 용도),
     * 세부 권한까지 전부 펼치면 관리자 계정에선 50줄이 넘어가 스크롤만 길어진다 — 사이드바와 같은 깊이다.
     * seed 는 목록이 처음 들어올 때 한 번만 — 그 뒤 사용자가 접은 걸 다시 펼치지 않는다.
     */
    const [openDomains, setOpenDomains] = useState(new Set());
    const [openTabs, setOpenTabs] = useState(new Set());
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current || tree.length === 0) return;
        seeded.current = true;
        setOpenDomains(new Set(tree.map((d) => d.domain)));
    }, [tree]);

    // 부서를 바꾸면 새로 생긴 도메인도 펼쳐준다 — 접힌 채로 나타나면 바뀐 걸 못 알아챈다.
    useEffect(() => {
        if (!seeded.current) return;
        setOpenDomains((prev) => {
            const next = new Set(prev);
            let grew = false;
            for (const d of tree) if (!next.has(d.domain)) { next.add(d.domain); grew = true; }
            return grew ? next : prev;
        });
    }, [tree]);

    const toggleOpen = (setter, key) => setter((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    if (tree.length === 0) {
        return <Typography sx={{ fontSize: 12, color: "#94a3b8", py: 1 }}>{emptyText}</Typography>;
    }

    // 출처 배지는 이름 바로 옆 — 편집 트리의 체크박스와 같은 자리라 두 트리가 같은 리듬으로 읽힌다.
    const PermRow = ({ p }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pr: ROW_PR, py: 0.4 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b", lineHeight: 1.4 }}>
                {p.nameKr}
            </Typography>
            {deptSet.has(p.code) && <SourceBadge label="부서" />}
            {personalSet.has(p.code) && <SourceBadge label="개인" />}
        </Box>
    );

    return (
        <Box>
            {tree.map((d) => {
                const dCodes = d.tabs.flatMap((t) => t.perms.map((p) => p.code));
                const dOpen = openDomains.has(d.domain);

                return (
                    <Box key={d.domain}>
                        {/* ── 도메인 행 ── */}
                        <Box
                            onClick={() => toggleOpen(setOpenDomains, d.domain)}
                            sx={{
                                display: "flex", alignItems: "center", gap: 0.75,
                                pl: DOMAIN_ROW_PL, pr: ROW_PR, py: 0.6, borderRadius: 1,
                                cursor: "pointer", userSelect: "none",
                                "&:hover": { bgcolor: "#f8fafc" },
                            }}
                        >
                            <Typography sx={{
                                fontSize: 12.5, fontWeight: 800, color: "#0f172a",
                                letterSpacing: 0.2, minWidth: 0,
                            }}>
                                {d.domain}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Count n={dCodes.length} />
                            <Expander open={dOpen} />
                        </Box>

                        {/* ── 탭 목록 ── */}
                        <Collapse in={dOpen} unmountOnExit>
                            <Box sx={{ pb: 0.5 }}>
                                <SubTreeLine inset={TREE_INSET}>
                                {d.tabs.map((t) => {
                                    const key = `${d.domain}/${t.tab}`;
                                    const tOpen = openTabs.has(key);

                                    // 권한이 1개뿐인 탭은 계층을 한 단 접는다 — 탭 행과 권한 행이 같은 말이 된다.
                                    if (t.perms.length === 1) {
                                        return <PermRow key={key} p={t.perms[0]} />;
                                    }

                                    return (
                                        <Box key={key}>
                                            <Box
                                                onClick={() => toggleOpen(setOpenTabs, key)}
                                                sx={{
                                                    display: "flex", alignItems: "center", gap: 0.75,
                                                    pr: ROW_PR, py: 0.45, borderRadius: 1,
                                                    cursor: "pointer", userSelect: "none",
                                                    "&:hover": { bgcolor: "#f8fafc" },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155", minWidth: 0 }}>
                                                    {t.tab}
                                                </Typography>
                                                <Box sx={{ flex: 1 }} />
                                                <Count n={t.perms.length} />
                                                <Expander open={tOpen} />
                                            </Box>

                                            <Collapse in={tOpen} unmountOnExit>
                                                <Box sx={{ pb: 0.25 }}>
                                                    <SubTreeLine inset={TREE_INSET}>
                                                        {t.perms.map((p) => (
                                                            <PermRow key={p.code} p={p} />
                                                        ))}
                                                    </SubTreeLine>
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                                </SubTreeLine>
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
}
